// src/calendar/AppointmentPrintSheet.js
// ── STANDALONE COMPONENT — DO NOT WIRE INTO CalendarPage YET ──────────────
// Props: { appointment, customer, employee, jobType, businessName, onClose }

import React from 'react';

const AppointmentPrintSheet = ({ appointment, customer, employee, jobType, businessName, onClose }) => {

  const fmtDate = (dt) => {
    if (!dt) return '—';
    try {
      return new Date(dt).toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch (_) { return String(dt); }
  };

  const fmtTime = (dt) => {
    if (!dt) return '—';
    try {
      return new Date(dt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch (_) { return String(dt); }
  };

  const handlePrint = () => {
    const content = document.getElementById('jf-print-sheet');
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Work Order — ${appointment?.title || 'Appointment'}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              background: white;
              color: black;
              font-size: 13px;
            }
            .header {
              border-bottom: 4px solid black;
              padding-bottom: 12px;
              margin-bottom: 16px;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
            }
            .business-name {
              font-size: 20px;
              font-weight: 900;
              text-transform: uppercase;
            }
            .doc-title {
              font-size: 16px;
              font-weight: 900;
              text-transform: uppercase;
              text-align: right;
              border: 3px solid black;
              padding: 6px 12px;
            }
            .section {
              margin-bottom: 14px;
              border: 2px solid black;
              padding: 10px 14px;
            }
            .section-title {
              font-size: 10px;
              font-weight: 900;
              text-transform: uppercase;
              color: #555;
              margin-bottom: 6px;
              letter-spacing: 1px;
            }
            .field-row {
              display: flex;
              gap: 24px;
              margin-bottom: 4px;
              flex-wrap: wrap;
            }
            .field {
              flex: 1;
              min-width: 180px;
            }
            .field-label {
              font-size: 10px;
              font-weight: 900;
              text-transform: uppercase;
              color: #777;
              margin-bottom: 2px;
            }
            .field-value {
              font-size: 14px;
              font-weight: 700;
            }
            .notes-box {
              border: 2px solid black;
              min-height: 80px;
              padding: 8px;
              margin-top: 4px;
              font-size: 13px;
            }
            .checkbox-section {
              border: 2px solid black;
              padding: 10px 14px;
              margin-bottom: 14px;
            }
            .checkbox-row {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-bottom: 8px;
              font-size: 13px;
              font-weight: 700;
            }
            .checkbox {
              width: 18px;
              height: 18px;
              border: 2px solid black;
              display: inline-block;
              flex-shrink: 0;
            }
            .signature-section {
              border-top: 3px solid black;
              margin-top: 20px;
              padding-top: 10px;
              display: flex;
              gap: 40px;
            }
            .sig-block {
              flex: 1;
            }
            .sig-line {
              border-bottom: 2px solid black;
              height: 36px;
              margin-bottom: 4px;
            }
            .sig-label {
              font-size: 10px;
              font-weight: 900;
              text-transform: uppercase;
              color: #555;
            }
            @media print {
              body { padding: 16px; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      zIndex: 6000, padding: '20px', overflowY: 'auto'
    },
    container: {
      backgroundColor: 'white', border: '8px solid black',
      padding: '24px', maxWidth: '720px', width: '100%', marginTop: '20px'
    },
    topBar: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: '16px', gap: '10px', flexWrap: 'wrap'
    },
    title: { fontWeight: 900, fontSize: '20px', textTransform: 'uppercase', minWidth: 0 },
    btnRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', maxWidth: '100%' },
    printBtn: {
      background: '#000000', color: '#E8C98F', border: '4px solid #E8761D',
      padding: '10px 20px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap'
    },
    closeBtn: {
      background: 'white', color: 'black', border: '4px solid black',
      padding: '10px 20px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap'
    },
    sheet: { fontFamily: 'Arial, sans-serif', fontSize: '13px', color: 'black' },
    header: {
      borderBottom: '4px solid black', paddingBottom: '12px', marginBottom: '16px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
    },
    bizName: { fontWeight: 900, fontSize: '20px', textTransform: 'uppercase', minWidth: 0, overflowWrap: 'anywhere' },
    docTitle: {
      fontWeight: 900, fontSize: '16px', textTransform: 'uppercase',
      border: '3px solid black', padding: '6px 12px', textAlign: 'right'
    },
    section: { marginBottom: '14px', border: '2px solid black', padding: '10px 14px' },
    sectionTitle: {
      fontSize: '10px', fontWeight: 900, textTransform: 'uppercase',
      color: '#555', marginBottom: '6px', letterSpacing: '1px'
    },
    fieldRow: { display: 'flex', gap: '24px', marginBottom: '4px', flexWrap: 'wrap' },
    field: { flex: 1, minWidth: '180px' },
    fieldLabel: { fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', color: '#777', marginBottom: '2px' },
    fieldValue: { fontSize: '14px', fontWeight: 700 },
    notesBox: { border: '2px solid black', minHeight: '80px', padding: '8px', marginTop: '4px', fontSize: '13px' },
    checkboxSection: { border: '2px solid black', padding: '10px 14px', marginBottom: '14px' },
    checkboxRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontSize: '13px', fontWeight: 700 },
    checkbox: { width: '18px', height: '18px', border: '2px solid black', display: 'inline-block', flexShrink: 0 },
    sigSection: { borderTop: '3px solid black', marginTop: '20px', paddingTop: '10px', display: 'flex', gap: '40px' },
    sigBlock: { flex: 1 },
    sigLine: { borderBottom: '2px solid black', height: '36px', marginBottom: '4px' },
    sigLabel: { fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', color: '#555' }
  };

  const appt = appointment || {};
  const cust = customer || {};
  const emp = employee || {};

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.container} onClick={(e) => e.stopPropagation()}>

        <div style={styles.topBar}>
          <div style={styles.title}>🖨️ WORK ORDER / SCHEDULE SLIP</div>
          <div style={styles.btnRow}>
            <button style={styles.printBtn} onClick={handlePrint}>PRINT NOW</button>
            <button style={styles.closeBtn} onClick={onClose}>CLOSE NOW</button>
          </div>
        </div>

        {/* ── Printable content ── */}
        <div id="jf-print-sheet" style={styles.sheet}>

          {/* Header */}
          <div style={styles.header}>
            <div style={styles.bizName}>{businessName || 'YOUR BUSINESS'}</div>
          </div>

          {/* Date & Time */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>📅 DATE & TIME</div>
            {appt.end_date && appt.end_date !== appt.date ? (
              <div style={styles.fieldRow}>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>START DATE</div>
                  <div style={styles.fieldValue}>{fmtDate(appt.start_time)}</div>
                </div>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>END DATE</div>
                  <div style={styles.fieldValue}>{fmtDate(appt.end_time)}</div>
                </div>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>START TIME</div>
                  <div style={styles.fieldValue}>{fmtTime(appt.start_time)}</div>
                </div>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>END TIME</div>
                  <div style={styles.fieldValue}>{fmtTime(appt.end_time)}</div>
                </div>
              </div>
            ) : (
              <div style={styles.fieldRow}>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>DATE</div>
                  <div style={styles.fieldValue}>{fmtDate(appt.start_time)}</div>
                </div>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>START TIME</div>
                  <div style={styles.fieldValue}>{fmtTime(appt.start_time)}</div>
                </div>
                <div style={styles.field}>
                  <div style={styles.fieldLabel}>END TIME</div>
                  <div style={styles.fieldValue}>{fmtTime(appt.end_time)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Customer */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>👤 CUSTOMER</div>
            <div style={styles.fieldRow}>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>NAME</div>
                <div style={styles.fieldValue}>{appt.customer_name || cust.name || '—'}</div>
              </div>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>OFFICE PHONE</div>
                <div style={styles.fieldValue}>{cust.phone || '—'}</div>
              </div>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>MOBILE PHONE</div>
                <div style={styles.fieldValue}>{cust.mobile_phone || cust.mobile || cust.mobilePhone || '—'}</div>
              </div>
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>SERVICE ADDRESS</div>
                <div style={styles.fieldValue}>
                  {cust.serviceAddress || cust.address || cust.street || '—'}
                  {cust.city ? ', ' + cust.city : ''}
                  {cust.state ? ', ' + cust.state : ''}
                  {cust.zip ? ' ' + cust.zip : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Job Info */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>🔧 JOB INFORMATION</div>
            <div style={styles.fieldRow}>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>JOB TYPE / SERVICE</div>
                <div style={styles.fieldValue}>{jobType || appt.title || '—'}</div>
              </div>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>ASSIGNED EMPLOYEE</div>
                <div style={styles.fieldValue}>{emp.name || '—'}</div>
              </div>
              <div style={styles.field}>
                <div style={styles.fieldLabel}>STATUS</div>
                <div style={styles.fieldValue}>{appt.status || 'SCHEDULED'}</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>📝 NOTES / DETAILS</div>
            <div style={styles.notesBox}>{appt.details || appt.notes || appt.description || ''}</div>
          </div>

          {/* Checkboxes */}
          <div style={styles.checkboxSection}>
            <div style={styles.sectionTitle}>✅ COMPLETION CHECKLIST</div>
            {['COMPLETED', 'PARTS NEEDED', 'FOLLOW UP REQUIRED', 'RESCHEDULE NEEDED'].map((item) => (
              <div key={item} style={styles.checkboxRow}>
                <div style={styles.checkbox}></div>
                <span>{item}</span>
              </div>
            ))}
          </div>

          {/* Signature */}
          <div style={styles.sigSection}>
            <div style={styles.sigBlock}>
              <div style={styles.sigLine}></div>
              <div style={styles.sigLabel}>TECHNICIAN SIGNATURE</div>
            </div>
            <div style={styles.sigBlock}>
              <div style={styles.sigLine}></div>
              <div style={styles.sigLabel}>CUSTOMER SIGNATURE</div>
            </div>
            <div style={styles.sigBlock}>
              <div style={styles.sigLine}></div>
              <div style={styles.sigLabel}>DATE COMPLETED</div>
            </div>
          </div>

        </div>
        {/* ── End printable content ── */}

      </div>
    </div>
  );
};

export default AppointmentPrintSheet;